/**
 * Presentación de la vigencia de la e.firma (FIEL).
 *
 * Vive aparte porque la usan dos lugares: la ficha completa de /credenciales y
 * el semáforo del encabezado (que la trae a la vista en toda la app, para que
 * nadie se entere de que venció hasta que falla el envío al SIM).
 */
import type { FielCertificado } from "./api";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

export function fmtFecha(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${String(d).padStart(2, "0")} ${MESES[m - 1]} ${y}`;
}

/** "1 año 3 meses" / "18 días" — para leer la vigencia de un vistazo. */
export function fmtPlazo(dias: number): string {
  const d = Math.abs(dias);
  if (d < 60) return `${d} día${d === 1 ? "" : "s"}`;
  const meses = Math.floor(d / 30.44);
  if (meses < 24) return `${meses} mes${meses === 1 ? "" : "es"}`;
  const anios = Math.floor(meses / 12);
  const resto = meses % 12;
  return resto ? `${anios} año${anios === 1 ? "" : "s"} ${resto} mes${resto === 1 ? "" : "es"}`
               : `${anios} año${anios === 1 ? "" : "s"}`;
}

export type TonoVigencia = "red" | "amber" | "emerald";

/** Umbral para avisar que ya conviene renovar la e.firma. */
export const DIAS_AVISO = 60;

/** Semáforo de la vigencia: rojo vencido, ámbar por vencer, verde. */
export function estadoVigencia(f: FielCertificado): { tono: TonoVigencia; texto: string } {
  const dias = f.dias_restantes ?? 0;
  if (f.vencido) return { tono: "red", texto: `venció hace ${fmtPlazo(dias)}` };
  if (dias <= DIAS_AVISO) return { tono: "amber", texto: `vence en ${fmtPlazo(dias)}` };
  return { tono: "emerald", texto: `vigente · faltan ${fmtPlazo(dias)}` };
}

/** Clases del badge por tono (completas: Tailwind no purga bien las interpoladas). */
export const BADGE_VIGENCIA: Record<TonoVigencia, string> = {
  red: "bg-red-100 text-red-700 border-red-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
};
